window.BAO.Carousel = {
  Element: class CarouselElement extends window.BAO.CustomElement() {
    static get requiredElements () {
      return ['slides', 'slide']
    }

    /**
     * @param {import('keen-slider').KeenSliderInstance|null} carousel
     */
    carousel = null

    connectedCallback () {
      super.connectedCallback()

      this.beforeInitialiseCarousel()

      this.carousel = this.initialiseCarousel()

      this.afterInitialiseCarousel()
    }

    beforeInitialiseCarousel () {
      this.attachCarouselClasses()
    }

    attachCarouselClasses () {
      this.els.slides.element.classList.add('keen-slider')
      this.els.slide.elements.forEach(slide => {
        slide.classList.add('keen-slider__slide')
      })
    }

    /**
     * @returns {import('keen-slider').KeenSliderInstance}
     */
    initialiseCarousel () {}

    afterInitialiseCarousel () {}
  },

  useInitialised () {
    /** @param {import('keen-slider').KeenSliderInstance} carousel */
    return carousel => {
      carousel.on('created', carousel => {
        carousel.container.setAttribute('data-initialised', true)
      })
    }
  },

  /**
   * @param {{
   *  next?: HTMLButtonElement,
   *  previous?: HTMLButtonElement,
   * }} elements
   */
  useArrows ({ next, previous } = {}) {
    /** @param {import('keen-slider').KeenSliderInstance} carousel */
    return carousel => {
      next?.addEventListener('click', carousel.next)
      previous?.addEventListener('click', carousel.prev)

      const updateButtonStates = () => {
        const { abs, maxIdx, min } = carousel.track.details

        if (next) {
          next.disabled = abs === maxIdx
          next.setAttribute('aria-hidden', (maxIdx === 0).toString())
        }

        if (previous) {
          previous.disabled = abs === min
          previous.setAttribute('aria-hidden', (maxIdx === 0).toString())
        }
      }

      carousel.on('created', updateButtonStates)
      carousel.on('slideChanged', updateButtonStates)
    }
  },

  /**
   * @param {number} seconds
   * @param {{
   *  pauseOnHover?: boolean
   * }} options
   */
  useAutoplay (seconds = 2, { pauseOnHover = true } = {}) {
    const delay = seconds * 1000

    /** @param {import('keen-slider').KeenSliderInstance} carousel */
    return carousel => {
      let timeout
      let mouseOver = false

      const clearCurrentTimout = () => clearTimeout(timeout)
      const setNextTimeout = () => {
        clearCurrentTimout()

        if (mouseOver) {
          return
        }

        timeout = setTimeout(carousel.next, delay)
      }

      carousel.on('created', () => {
        if (pauseOnHover) {
          carousel.container.addEventListener('mouseover', () => {
            mouseOver = true
            clearCurrentTimout()
          })
          carousel.container.addEventListener('mouseout', () => {
            mouseOver = false
            setNextTimeout()
          })
        }

        setNextTimeout()
      })

      carousel.on('dragStarted', clearCurrentTimout)
      carousel.on('animationEnded', setNextTimeout)
      carousel.on('updated', setNextTimeout)
    }
  },

  /** @param {HTMLButtonElement[]} dots */
  /** @param {Function} dotUpdateCallback */
  useDots (dots = [], dotUpdateCallback = (carousel, dot, index) => {}) {
    /** @param {import('keen-slider').KeenSliderInstance} carousel */
    return carousel => {
      const updateDotStates = () => {
        dots.forEach((dot, index) => {
          const isCurrent = carousel.track.details.rel === index

          dot.setAttribute('aria-current', String(isCurrent))

          if (dotUpdateCallback !== undefined) {
            dotUpdateCallback(carousel, dot, index)
          }
        })
      }

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          carousel.moveToIdx(index)
        })
      })

      carousel.on('created', updateDotStates)
      carousel.on('slideChanged', updateDotStates)
    }
  },

  /** @param {HTMLButtonElement[]} thumbnails */
  /** @param {Function} dotUpdateCallback */
  useThumbnails (thumbnails = [], dotUpdateCallback = (carousel, thumbnail, index) => {}) {
    /** @param {import('keen-slider').KeenSliderInstance} carousel */
    return carousel => {
      const updateThumbnailStates = () => {
        thumbnails.forEach((thumbnail, index) => {
          const isCurrent = carousel.track.details.rel === index

          thumbnail.setAttribute('aria-current', String(isCurrent))

          if (dotUpdateCallback !== undefined) {
            dotUpdateCallback(carousel, thumbnail, index)
          }
        })
      }

      thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => {
          carousel.moveToIdx(index)
        })
      })

      carousel.on('created', updateThumbnailStates)
      carousel.on('slideChanged', updateThumbnailStates)
    }
  },

  useSetCurrentImageStyle (containerElement) {
    /** @param {import('keen-slider').KeenSliderInstance} carousel */
    return carousel => {
      const setSlideImageStyles = async () => {
        await Promise.all(
          carousel.slides.map(async slide => {
            const img = slide.querySelector('img')
            if (!img) return

            slide.dataset.imageStyle = await window.BAO.Utils.isThisImageDarkOrLight(
              img,
            )
          }),
        )

        setCurrentSlideImageStyle()
      }

      const setCurrentSlideImageStyle = () => {
        const currentSlide = carousel.slides[carousel.track.details.abs]
        containerElement.dataset.imageStyle = currentSlide.dataset.imageStyle
      }

      carousel.on('created', setSlideImageStyles)
      carousel.on('slideChanged', setCurrentSlideImageStyle)
    }
  },
}
